import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, inr, fmtDate, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Repeat, Truck, CheckCircle2, Clock, Zap } from "lucide-react";

const Row = ({ l, v }) => (
  <div className="flex items-start justify-between gap-3 py-1 border-b border-slate-100 last:border-0">
    <span className="text-[11px] uppercase tracking-wider text-slate-400 pt-0.5 shrink-0">{l}</span>
    <span className="text-sm text-slate-900 text-right">{v}</span>
  </div>
);

const TABS = [
  { key: "active", label: "Active Orders", icon: Clock },
  { key: "completed", label: "Completed Orders", icon: CheckCircle2 },
];

const StatusChip = ({ so }) => {
  const label = so.timeline[so.stage_index];
  const delivered = so.stage_index === so.timeline.length - 1;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1 border ${
      delivered ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-[#FC5F5F]/10 text-[#C94A4A] border-[#FC5F5F]/25"}`}
      data-testid="order-status-chip">{label}</span>
  );
};

export default function PortalOrders() {
  const [list, setList] = useState([]);
  const [repeatFor, setRepeatFor] = useState(null); // {so, sel: {idx: qty}, remarks}
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "active";
  useEffect(() => { api.get("/portal/orders").then((r) => setList(r.data)).catch(() => {}); }, []);

  const classify = (so) => {
    if (so.status === "Cancelled") return "cancelled"; // hidden — no tab
    if (so.status === "Delivered" || so.stage_index === so.timeline.length - 1) return "completed";
    return "active";
  };
  const counts = list.reduce((a, so) => { const k = classify(so); a[k] = (a[k] || 0) + 1; return a; }, {});
  const shown = list.filter((so) => classify(so) === tab);

  const openRepeat = (so) => {
    const sel = {};
    (so.items || []).forEach((it, i) => { sel[i] = String(+it.quantity || ""); });
    setRepeatFor({ so, sel, remarks: "" });
  };
  const toggleItem = (i) => setRepeatFor((r) => {
    const sel = { ...r.sel };
    if (sel[i] === undefined) sel[i] = String(+r.so.items[i].quantity || "");
    else delete sel[i];
    return { ...r, sel };
  });
  const reorderAll = async (so) => {
    if (!window.confirm(`Reorder all ${(so.items || []).length} product(s) from ${so.number} at the same quantities?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/portal/repeat-order", {
        source_type: "order", source_id: so.id, remarks: `One-tap reorder of ${so.number}`, items: [],
      });
      toast.success(r.data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const submitRepeat = async () => {
    const items = Object.entries(repeatFor.sel).map(([i, q]) => ({ index: +i, quantity: +q || 0 }));
    if (!items.length) { toast.error("Select at least one product"); return; }
    setBusy(true);
    try {
      const r = await api.post("/portal/repeat-order", {
        source_type: "order", source_id: repeatFor.so.id, remarks: repeatFor.remarks, items,
      });
      toast.success(r.data.message);
      setRepeatFor(null);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="portal-orders">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-display font-bold text-slate-900 tracking-tight">Your Orders</h1>
        <div className="flex items-center gap-1.5" data-testid="portal-orders-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setParams(t.key === "active" ? {} : { tab: t.key })}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-1.5 border transition-colors duration-200 ${
                      tab === t.key ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-white text-slate-500 border-[#3B82F6]/15 hover:border-[#3B82F6]/40"}`}
                    data-testid={`po-tab-${t.key}`}>
              <t.icon size={12} /> {t.label} ({counts[t.key] || 0})
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {shown.map((so) => (
          <div key={so.id} className="border border-slate-200 rounded-xl bg-white p-4" data-testid={`po-card-${so.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-sm font-semibold text-slate-900">{so.number}</span>
              <StatusChip so={so} />
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Ordered: {fmtDate(so.order_date || so.created_at)}{so.po_number ? ` · Your PO: ${so.po_number}` : ""}
            </div>
            {so.expected_dispatch_date && classify(so) === "active" && (
              <div className="mt-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1"
                      data-testid={`po-expected-${so.id}`}>
                  <Truck size={11} /> Expected dispatch by {fmtDate(so.expected_dispatch_date)} (5–6 working days)
                </span>
              </div>
            )}

            <div className="mt-3 space-y-3">
              {(so.items || []).map((it, i) => (
                <div key={i} className="border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
                  <Row l="Product" v={it.description || it.product_name || "-"} />
                  <Row l="Colour" v={it.color || "-"} />
                  {it.cut_length ? <Row l="Cut Length" v={it.cut_length} /> : null}
                  <Row l="Size" v={it.size || it.size_label || "-"} />
                  {it.tc_size ? <Row l="Notes" v={it.tc_size} /> : null}
                  <Row l="Qty" v={`${+it.quantity || 0}${it.unit ? ` ${it.unit}` : ""}`} />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
              <div className="text-sm font-bold text-slate-900 tabular-nums">{inr(so.total)}</div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => reorderAll(so)} disabled={busy}
                        className="inline-flex items-center gap-1 text-xs font-semibold bg-[#3B82F6] text-white rounded-md px-3 py-1.5 hover:bg-[#2563EB] transition-colors duration-200 disabled:opacity-50"
                        data-testid={`po-reorder-all-${so.id}`}>
                  <Zap size={12} /> Reorder All</button>
                <button onClick={() => openRepeat(so)} className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded-md px-3 py-1.5 text-slate-600 hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors duration-200" data-testid={`po-repeat-${so.id}`}>
                  <Repeat size={12} /> Pick items</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {shown.length === 0 && (
        <p className="text-sm text-slate-400 py-8 text-center bg-white border border-slate-200 rounded-xl" data-testid="po-empty">
          {list.length === 0 ? "No orders yet." : `No ${tab} orders.`}
        </p>
      )}

      {repeatFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={() => setRepeatFor(null)}>
          <div role="dialog" aria-modal="true" className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="repeat-dialog">
            <h3 className="font-display font-bold text-slate-900">Repeat Order · {repeatFor.so.number}</h3>
            <p className="text-xs text-slate-500 mt-1 mb-4">Untick the products you don't need, and adjust quantities.</p>
            <div className="space-y-2">
              {repeatFor.so.items.map((it, i) => {
                const checked = repeatFor.sel[i] !== undefined;
                return (
                  <div key={i} className={`flex items-center gap-3 border rounded-xl px-3.5 py-3 transition-colors duration-200 ${checked ? "border-[#3B82F6]/25 bg-slate-50/60" : "border-slate-200 opacity-60"}`} data-testid={`repeat-item-${i}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleItem(i)}
                           className="w-4 h-4 accent-[#3B82F6] cursor-pointer" data-testid={`repeat-check-${i}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{it.description || it.product_name}</div>
                      <div className="text-[11px] text-slate-500">{it.size || it.size_label}{it.color ? ` · ${it.color}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-none">
                      <input type="number" min="0" disabled={!checked} value={checked ? repeatFor.sel[i] : ""}
                             onChange={(e) => setRepeatFor((r) => ({ ...r, sel: { ...r.sel, [i]: e.target.value } }))}
                             className="w-20 h-9 text-sm border border-slate-200 rounded-md px-2 text-right disabled:bg-slate-50"
                             data-testid={`repeat-qty-${i}`} />
                      <span className="text-[11px] text-slate-400 w-10">{it.unit}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <textarea className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mt-3" rows={2}
                      placeholder="Any notes for our team (optional)" value={repeatFor.remarks}
                      onChange={(e) => setRepeatFor((r) => ({ ...r, remarks: e.target.value }))} data-testid="repeat-remarks" />
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-slate-400">{Object.keys(repeatFor.sel).length} of {repeatFor.so.items.length} products selected</span>
              <div className="flex gap-2">
                <button onClick={() => setRepeatFor(null)} className="px-4 py-2.5 border border-slate-200 rounded-full text-sm">Cancel</button>
                <button onClick={submitRepeat} disabled={busy || Object.keys(repeatFor.sel).length === 0}
                        className="inline-flex items-center gap-1.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-md px-6 py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="repeat-submit">
                  <Repeat size={13} /> {busy ? "Sending…" : "Place Repeat Order"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
